












































































import { computed, defineComponent, PropType, ref } from "@vue/composition-api";
import {
    ButtonCategory,
    ButtonType,
    TemplateComponentButton,
    TemplateComponentsInterface,
} from "@/interfaces/template.interface";
import TemplateContentButtonCtaUrl from "@/views/modules/settings/whatsapp-templates/components/templateContentButtonCtaUrl.vue";
import { BButton, BFormGroup, BFormSelect, VBTooltip } from "bootstrap-vue";
import TemplateContentButtonCtaPhone from "@/views/modules/settings/whatsapp-templates/components/templateContentButtonCtaPhone.vue";
import TemplateContentButtonQuickReply from "@/views/modules/settings/whatsapp-templates/components/templateContentButtonQuickReply.vue";
// @ts-ignore
import i18n from "@/libs/i18n";
import useMessageTemplates from "@/composables/useMessageTemplates";

export default defineComponent({
    components: {
        BFormGroup,
        BFormSelect,
        TemplateContentButtonQuickReply,
        TemplateContentButtonCtaPhone,
        BButton,
        TemplateContentButtonCtaUrl,
    },
    directives: {
        "b-tooltip": VBTooltip,
    },
    props: {
        value: {
            // Buttons do not have to be defined
            required: false,
            type: Object as PropType<TemplateComponentsInterface["buttons"]>,
        },
    },
    setup(props, { emit }) {
        const { buttonTypeOptions } = useMessageTemplates();

        const buttons = computed<TemplateComponentButton[]>({
            get: () => props.value?.buttons ?? [],
            set: (newButtons) =>
                emit("input", newButtons.length ? { type: "BUTTONS", buttons: newButtons } : undefined),
        });

        const _buttonTypeSelected = ref<ButtonCategory | null>();

        const buttonTypeSelected = computed<ButtonCategory | null>({
            get: () => {
                // If buttons are empty and there is an internal button type select that
                if (typeof _buttonTypeSelected.value !== "undefined" && !buttons.value.length) {
                    return _buttonTypeSelected.value;
                }

                // If there is no internal value and no buttons assume they are empty
                if (!buttons.value.length) {
                    return null;
                }

                // Determine category by the first button
                return buttons.value[0].type === ButtonType.REPLY
                    ? ButtonCategory.REPLY
                    : ButtonCategory.CALL_TO_ACTION;
            },
            set: (value) => {
                // Set internal value to determine button type if it is empty
                _buttonTypeSelected.value = value;
            },
        });

        const canChangeCtaButtonType = computed(
            () => buttons.value.filter((button) => button.type === "PHONE_NUMBER" || button.type === "URL").length <= 1
        );

        const canNotAddButton = computed(
            () =>
                (buttonTypeSelected.value === ButtonCategory.CALL_TO_ACTION && buttons.value.length >= 2) ||
                (buttonTypeSelected.value === ButtonCategory.REPLY && buttons.value.length >= 3)
        );

        const buttonTypeDescription = computed(() =>
            buttonTypeSelected.value === ButtonCategory.CALL_TO_ACTION
                ? i18n.t("settings.templates.whatsapp.content.buttons.description-call-to-action")
                : i18n.t("settings.templates.whatsapp.content.buttons.description-quick-reply")
        );

        const addButton = () => {
            if (canNotAddButton.value) {
                return;
            }

            if (buttonTypeSelected.value === ButtonCategory.REPLY) {
                buttons.value = [...buttons.value, createEmptyButtonFromType(ButtonType.REPLY)];
                return;
            }

            // Check if another CTA button is already existing with one of the other options

            const otherCtoButtonIndex = buttons.value.find(
                (button) => button.type === ButtonType.PHONE || button.type === ButtonType.URL
            )?.type;

            if (!otherCtoButtonIndex) {
                buttons.value = [...buttons.value, createEmptyButtonFromType(ButtonType.PHONE)];

                return;
            }

            switch (otherCtoButtonIndex) {
                case ButtonType.PHONE:
                    buttons.value = [...buttons.value, createEmptyButtonFromType(ButtonType.URL)];
                    return;
                case ButtonType.URL:
                    buttons.value = [...buttons.value, createEmptyButtonFromType(ButtonType.PHONE)];
                    return;
                default:
                    throw new Error("Unexpected behaviour");
            }
        };

        function createEmptyButtonFromType(buttonType: ButtonType): TemplateComponentButton {
            switch (buttonType) {
                case ButtonType.URL:
                    return {
                        type: ButtonType.URL,
                        text: "",
                        url: "",
                        dynamic: false,
                    };
                case ButtonType.PHONE:
                    return {
                        type: ButtonType.PHONE,
                        text: "",
                        phone_number: "",
                    };
                case ButtonType.REPLY:
                    return {
                        type: ButtonType.REPLY,
                        text: "",
                    };
            }
        }

        const deleteButton = (buttonIndex: number) => {
            buttons.value.splice(buttonIndex, 1);
            buttons.value = [...buttons.value];
        };

        const changeButton = (buttonIndex: number, buttonType: ButtonType) => {
            if (buttonType === buttons.value[buttonIndex].type) {
                return;
            }
            buttons.value[buttonIndex] = createEmptyButtonFromType(buttonType);
            buttons.value = [...buttons.value];
        };

        return {
            ButtonType,

            buttons,
            buttonTypeDescription,
            buttonTypeSelected,
            canChangeCtaButtonType,
            canNotAddButton,
            buttonTypeOptions,

            addButton,
            deleteButton,
            changeButton,
        };
    },
});
